import React, { useRef, useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import Chart from "chart.js/auto";
import { getRelativePosition } from "chart.js/helpers";
import { getChartTheme, chartColorPalettes } from "../../utils/designSystem";
import { Card } from "../ui/Card";

/**
 * AdvancedChart Component
 *
 * A highly customizable chart component with enhanced styling, animations,
 * and interactivity features. Supports multiple chart types and advanced
 * configuration options.
 *
 * @param {Object} props
 * @param {string} props.type - Chart type (bar, line, pie, doughnut, radar, polarArea, bubble, scatter)
 * @param {Object} props.data - Chart data
 * @param {string} props.title - Chart title
 * @param {boolean} props.gradient - Whether to use gradient fills
 * @param {boolean} props.interactive - Whether to enable interactive features
 * @param {Object} props.customOptions - Custom Chart.js options
 * @param {Function} props.onDataPointClick - Callback for data point click
 * @param {string} props.className - Additional CSS classes
 */
const AdvancedChart = ({
  type = "bar",
  data,
  title,
  gradient = true,
  interactive = true,
  customOptions = {},
  onDataPointClick,
  className = "",
  height = 300,
}) => {
  const { isDarkMode } = useTheme();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [hoveredDataIndex, setHoveredDataIndex] = useState(null);

  // Theme colors from design system
  const themeColors = getChartTheme(isDarkMode);

  // Use design system color palettes
  const colorPalettes = chartColorPalettes;

  // Create gradient for datasets
  const createGradient = (ctx, colorSet) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);
    gradient.addColorStop(0, colorSet.border);
    gradient.addColorStop(1, themeColors.backgroundColor);
    return gradient;
  };

  useEffect(() => {
    if (!chartRef.current || !data) return;

    const ctx = chartRef.current.getContext("2d");

    // Destroy previous chart instance if exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Process datasets with colors and gradients
    const processedData = {
      ...data,
      datasets: Array.isArray(data?.datasets)
        ? data.datasets.map((dataset, index) => {
            const colorSet =
              dataset.colorSet && colorPalettes[dataset.colorSet]
                ? colorPalettes[dataset.colorSet][
                    index % colorPalettes[dataset.colorSet].length
                  ]
                : colorPalettes.default[index % colorPalettes.default.length];

            const backgroundColor =
              gradient &&
              (type === "line" || type === "bar" || type === "radar") &&
              ctx
                ? createGradient(ctx, colorSet)
                : isDarkMode
                  ? colorSet.dark
                  : colorSet.light;

            return {
              ...dataset,
              backgroundColor: dataset.backgroundColor || backgroundColor,
              borderColor: dataset.borderColor || colorSet.border,
              borderWidth: dataset.borderWidth || (type === "line" ? 2 : 1),
              pointBackgroundColor:
                dataset.pointBackgroundColor ||
                (isDarkMode ? "#1e293b" : "#ffffff"),
              pointBorderColor: dataset.pointBorderColor || colorSet.border,
              pointHoverBackgroundColor:
                dataset.pointHoverBackgroundColor || colorSet.border,
              pointHoverBorderColor:
                dataset.pointHoverBorderColor ||
                (isDarkMode ? "#ffffff" : "#1e293b"),
              tension: dataset.tension || (type === "line" ? 0.4 : 0),
              fill:
                dataset.fill !== undefined
                  ? dataset.fill
                  : type === "line"
                    ? "origin"
                    : undefined,
            };
          })
        : [],
    };

    // Default options based on chart type
    const defaultOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 1000,
        easing: "easeOutQuart",
      },
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          position: "top",
          labels: {
            color: themeColors.textColor,
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
            },
          },
        },
        title: {
          display: !!title,
          text: title,
          color: themeColors.textColor,
          font: {
            size: 16,
            weight: "bold",
          },
          padding: {
            top: 10,
            bottom: 20,
          },
        },
        tooltip: {
          backgroundColor: themeColors.tooltipBackground,
          titleColor: isDarkMode ? "#e2e8f0" : "#1e293b",
          bodyColor: isDarkMode ? "#e2e8f0" : "#1e293b",
          borderColor: themeColors.tooltipBorder,
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          boxPadding: 6,
          usePointStyle: true,
          callbacks: {
            // Custom label formatting can be added here
          },
        },
      },
      scales:
        type === "radar" ||
        type === "pie" ||
        type === "doughnut" ||
        type === "polarArea"
          ? undefined
          : {
              x: {
                grid: {
                  color: themeColors.gridColor,
                  drawBorder: false,
                  drawTicks: false,
                },
                ticks: {
                  color: themeColors.textColor,
                  padding: 10,
                  font: {
                    size: 11,
                  },
                },
                border: {
                  display: false,
                },
              },
              y: {
                grid: {
                  color: themeColors.gridColor,
                  drawBorder: false,
                  drawTicks: false,
                },
                ticks: {
                  color: themeColors.textColor,
                  padding: 10,
                  font: {
                    size: 11,
                  },
                },
                border: {
                  display: false,
                },
                beginAtZero: true,
              },
            },
    };

    // Merge options
    const options = {
      ...defaultOptions,
      ...customOptions,
    };

    // Create chart instance
    chartInstance.current = new Chart(ctx, {
      type,
      data: processedData,
      options,
    });

    // Add click event handler if provided
    if (interactive && onDataPointClick) {
      chartRef.current.onclick = (evt) => {
        const points = chartInstance.current.getElementsAtEventForMode(
          evt,
          "nearest",
          { intersect: true },
          false,
        );

        if (points.length) {
          const firstPoint = points[0];
          const dataIndex = firstPoint.index;
          const datasetIndex = firstPoint.datasetIndex;
          const value =
            chartInstance.current.data.datasets[datasetIndex].data[dataIndex];
          const label = chartInstance.current.data.labels[dataIndex];

          onDataPointClick({
            dataIndex,
            datasetIndex,
            value,
            label,
            dataset: chartInstance.current.data.datasets[datasetIndex],
          });
        }
      };
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [
    data,
    type,
    title,
    isDarkMode,
    gradient,
    interactive,
    customOptions,
    onDataPointClick,
  ]);

  return (
    <Card variant="performance" className={className} hoverable={true}>
      <div className="p-5">
        <div className={`relative`} style={{ height: `${height}px` }}>
          <canvas ref={chartRef} />
        </div>
      </div>
    </Card>
  );
};

export default React.memo(AdvancedChart, (prevProps, nextProps) => {
  // Custom comparison function for deep comparison of data
  return (
    prevProps.type === nextProps.type &&
    prevProps.title === nextProps.title &&
    prevProps.gradient === nextProps.gradient &&
    prevProps.interactive === nextProps.interactive &&
    prevProps.colorPalette === nextProps.colorPalette &&
    prevProps.height === nextProps.height &&
    JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
    JSON.stringify(prevProps.customOptions) ===
      JSON.stringify(nextProps.customOptions)
  );
});
